import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Review one vendor's regularization answer: what the record held when the link was sent
 * (request.original_data, grouped by section) against what the vendor submitted
 * (request.updated_data — flat, and only the fields they changed).
 *
 * Every submitted field is shown. The link can ask for GST, PAN, bank, MSME / Udyam or documents
 * alone, so a section-by-section view that skipped any of them would have an admin approve
 * changes they never saw; anything outside the known sections lands under "Other".
 */

const MASKED = '***';

const SECTIONS = [
  {
    id: 'company',
    label: 'Company',
    original: 'company_info',
    fields: {
      company_name: 'Company name',
      business_vertical: 'Business vertical',
      contact_person_name: 'Contact person',
      designation: 'Designation',
      email: 'Email',
      phone_number: 'Phone number',
      website: 'Website',
      business_description: 'Business description',
      year_established: 'Year established',
      director_name: 'Director name',
      director_email: 'Director email',
      alt_spoc_name: 'Alternate SPOC',
      alt_spoc_email: 'Alternate SPOC email',
      alt_spoc_designation: 'Alternate SPOC designation',
      alt_spoc_phone: 'Alternate SPOC phone',
    },
  },
  {
    id: 'address',
    label: 'Addresses',
    original: 'addresses',
    fields: {
      registered_address: 'Registered address',
      registered_city: 'Registered city',
      registered_district: 'Registered district',
      registered_state: 'Registered state',
      registered_country: 'Registered country',
      registered_pincode: 'Registered pincode',
      supply_address: 'Supply address',
      supply_city: 'Supply city',
      supply_district: 'Supply district',
      supply_state: 'Supply state',
      supply_country: 'Supply country',
      supply_pincode: 'Supply pincode',
    },
  },
  {
    id: 'compliance',
    label: 'Tax & registration',
    original: 'compliance',
    fields: {
      gst_number: 'GSTIN',
      pan_number: 'PAN',
      tan_number: 'TAN',
      gta_registration: 'GTA registration',
      nature_of_assessee: 'Nature of assessee',
      place_of_supply: 'Place of supply',
      tax_registration_number: 'Tax registration number',
      vat_number: 'VAT number',
      business_license: 'Business licence',
    },
  },
  {
    id: 'bank',
    label: 'Bank details',
    original: 'bank_details',
    fields: {
      bank_name: 'Bank name',
      branch_name: 'Branch name',
      account_number: 'Account number',
      account_type: 'Account type',
      ifsc_code: 'IFSC code',
      swift_code: 'SWIFT code',
      bank_address: 'Bank address',
    },
  },
  {
    id: 'msme',
    label: 'MSME / Udyam',
    original: 'msme',
    fields: {
      msme_status: 'MSME status',
      msme_category: 'MSME category',
      msme_number: 'Udyam number',
      classification_year: 'Classification year',
      classification_date: 'Classification date',
    },
  },
  {
    id: 'financial',
    label: 'Financial',
    original: 'financial',
    fields: {
      annual_turnover: 'Annual turnover',
      currency: 'Currency',
      employee_count: 'Employee count',
    },
  },
];

const KNOWN = new Set(SECTIONS.flatMap((s) => Object.keys(s.fields)));

const humanize = (key) => key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const show = (value) => {
  if (value === undefined || value === null || value === '') return 'Not specified';
  if (value === MASKED) return 'On record (hidden)';
  if (typeof value === 'object') {
    const text = JSON.stringify(value);
    return text.length > 400 ? `${text.slice(0, 399)}…` : text;
  }
  return String(value);
};

/** The submitted changes, grouped: [{ id, label, rows: [{ key, label, from, to }] }]. */
function groupChanges(originalData, updatedData) {
  const groups = SECTIONS.map((s) => {
    const original = originalData[s.original] || {};
    const rows = Object.entries(s.fields)
      .filter(([key]) => updatedData[key] !== undefined)
      .map(([key, label]) => ({ key, label, from: original[key], to: updatedData[key] }));
    return { id: s.id, label: s.label, rows };
  });
  const other = Object.keys(updatedData)
    .filter((key) => !KNOWN.has(key))
    .map((key) => ({ key, label: humanize(key), from: undefined, to: updatedData[key] }));
  groups.push({ id: 'other', label: 'Other', rows: other });
  return groups;
}

const ComparisonModal = ({ request, onClose, onVerify }) => {
  const groups = useMemo(
    () => groupChanges(request.original_data || {}, request.updated_data || {}),
    [request.original_data, request.updated_data]
  );
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const [activeTab, setActiveTab] = useState(() => (groups.find((g) => g.rows.length > 0) || groups[0]).id);
  const active = groups.find((g) => g.id === activeTab) || groups[0];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-200" onClick={onClose} />
      <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[calc(95vh/var(--app-scale))] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                Review Changes - {request.vendor.company_name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {total === 0
                  ? 'The vendor submitted without changing anything on record.'
                  : `${total} field${total === 1 ? '' : 's'} changed — what the record held when the link was sent, against what the vendor submitted.`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 px-6 overflow-x-auto">
            <div className="flex gap-2">
              {groups
                .filter((g) => g.id !== 'other' || g.rows.length > 0)
                .map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setActiveTab(g.id)}
                    className={`whitespace-nowrap px-4 py-3 border-b-2 font-medium transition-colors ${
                      active.id === g.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {g.label}
                    {g.rows.length > 0 && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{g.rows.length}</span>
                    )}
                  </button>
                ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {active.rows.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No changes in this section</p>
            ) : (
              <div className="space-y-4">
                {active.rows.map((row) => (
                  <div key={row.key} className="border rounded-lg p-4 bg-amber-50 border-amber-300">
                    <h4 className="font-medium text-gray-900 mb-3">{row.label}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">On record when the link was sent:</p>
                        <p className="text-sm text-green-700 font-medium break-words">{active.id === 'other' ? '—' : show(row.from)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Submitted by the vendor:</p>
                        <p className="text-sm font-medium text-red-700 break-words">{show(row.to)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onVerify} className="bg-emerald-600 hover:bg-emerald-700">
              <Icon name="CheckCircle" size={18} className="mr-2" />
              Verify & Approve
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparisonModal;
